// W-0829 — Página: Resultado de Atendimento (Imob > Cobrador).
// Tela única mestre/detalhe: grid de Tipos (topo) + grid de SubTipos (abaixo), carregado
// automaticamente ao selecionar um Tipo (RN-9). Fonte Delphi: UResultadoAtend.pas (TFResultadoAtend).
//
// LICOES aplicadas:
// - key estável: Tipos `tipo-${codigo}`; SubTipos `sub-${cod_atendimento}-${codigo}` (PK composta).
// - err.message direto, porque o interceptor já faz unwrap do erro.
// - Negação de permissão vem como 400 (NÃO 403 — SEC-005): toast, nunca logout.
// - Sem cache: cada write recarrega a lista afetada (cadastro mutável).

import React, { useState, useEffect, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { MSG_OPERACAO_CONCLUIDA } from '~/constant';
import GridTipos from './components/GridTipos';
import GridSubTipos from './components/GridSubTipos';
import ModalFormTipo from './components/ModalFormTipo';
import ModalFormSubTipo from './components/ModalFormSubTipo';
import {
  listarTipos,
  inserirTipo,
  editarTipo,
  excluirTipo,
  listarSubTipos,
  inserirSubTipo,
  editarSubTipo,
  excluirSubTipo,
} from './services/resultadoAtendimentoService';

const extrairMensagemErro = (err) =>
  (err && err.message) ||
  'Ocorreu um erro inesperado.';

const ResultadoAtendimento = () => {
  const [tipos, setTipos] = useState([]);
  const [tipoSelecionado, setTipoSelecionado] = useState(null);
  const [subTipos, setSubTipos] = useState([]);
  const [subTipoSelecionado, setSubTipoSelecionado] = useState(null);

  // modais
  const [modalTipo, setModalTipo] = useState({ show: false, tipo: null });
  const [modalSubTipo, setModalSubTipo] = useState({ show: false, subTipo: null });
  const [modalExcluir, setModalExcluir] = useState({
    show: false,
    alvo: null, // 'tipo' | 'subtipo'
    item: null,
    message: '',
  });

  const carregarTipos = useCallback(async (filtro, tipoBusca, autoSelecionar) => {
    try {
      const lista = (await listarTipos(filtro, tipoBusca)) || [];
      setTipos(lista);
      if (autoSelecionar && lista.length > 0) {
        // Paridade FormShow: auto-seleciona o 1º Tipo na abertura.
        selecionarTipo(lista[0]);
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarSubTipos = useCallback(async (codAtendimento) => {
    try {
      const lista = (await listarSubTipos(codAtendimento)) || [];
      setSubTipos(lista);
      setSubTipoSelecionado(null);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  }, []);

  const selecionarTipo = useCallback(
    (tipo) => {
      setTipoSelecionado(tipo);
      setSubTipoSelecionado(null);
      if (tipo) carregarSubTipos(tipo.codigo);
      else setSubTipos([]);
    },
    [carregarSubTipos]
  );

  // Abertura — lista Tipos (range 100..999) e auto-seleciona o 1º.
  useEffect(() => {
    carregarTipos(null, 'descricao', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiltrar = (filtro, tipoBusca) => {
    carregarTipos(filtro, tipoBusca, false);
  };

  // ---- Tipo: CRUD ----
  const handleConfirmarTipo = async (data) => {
    try {
      if (data.codigo != null) await editarTipo(data);
      else await inserirTipo(data);
      setModalTipo({ show: false, tipo: null });
      toast.success(MSG_OPERACAO_CONCLUIDA);
      const cod = data.codigo != null ? data.codigo : null;
      const lista = (await listarTipos(null, 'descricao')) || [];
      setTipos(lista);
      // Mantém a seleção no registro editado quando possível.
      if (cod != null) {
        const reSel = lista.find((t) => t.codigo === cod);
        if (reSel) setTipoSelecionado(reSel);
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  const handleExcluirTipo = (tipo) => {
    setModalExcluir({
      show: true,
      alvo: 'tipo',
      item: tipo,
      message: `Deseja excluir o tipo "${tipo.descricao}"?`,
    });
  };

  // ---- SubTipo: CRUD ----
  const handleConfirmarSubTipo = async (data) => {
    try {
      if (data.codigo != null) await editarSubTipo(data);
      else await inserirSubTipo(data);
      setModalSubTipo({ show: false, subTipo: null });
      toast.success(MSG_OPERACAO_CONCLUIDA);
      if (tipoSelecionado) carregarSubTipos(tipoSelecionado.codigo);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  const handleExcluirSubTipo = (sub) => {
    setModalExcluir({
      show: true,
      alvo: 'subtipo',
      item: sub,
      message: `Deseja excluir subtipo ${sub.descricao}?`,
    });
  };

  // ---- confirmação de exclusão (Tipo ou SubTipo) ----
  const handleFecharExcluir = async (confirmado) => {
    const { alvo, item } = modalExcluir;
    setModalExcluir({ show: false, alvo: null, item: null, message: '' });
    if (!confirmado || !item) return;
    try {
      if (alvo === 'tipo') {
        await excluirTipo(item.codigo);
        toast.success(MSG_OPERACAO_CONCLUIDA);
        // Tipo excluído: limpa seleção e recarrega.
        setTipoSelecionado(null);
        setSubTipos([]);
        const lista = (await listarTipos(null, 'descricao')) || [];
        setTipos(lista);
      } else {
        await excluirSubTipo({ codigo: item.codigo, cod_atendimento: item.cod_atendimento });
        toast.success(MSG_OPERACAO_CONCLUIDA);
        if (tipoSelecionado) carregarSubTipos(tipoSelecionado.codigo);
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  return (
    <>
      <Header title="Resultado de Atendimento" />
      <Container fluid className="py-3">
        <GridTipos
          tipos={tipos}
          tipoSelecionado={tipoSelecionado}
          onSelecionarTipo={selecionarTipo}
          onFiltrar={handleFiltrar}
          onIncluir={() => setModalTipo({ show: true, tipo: null })}
          onEditar={(tipo) => setModalTipo({ show: true, tipo })}
          onExcluir={handleExcluirTipo}
        />

        <GridSubTipos
          subTipos={subTipos}
          tipoSelecionado={tipoSelecionado}
          subTipoSelecionado={subTipoSelecionado}
          onSelecionarSubTipo={setSubTipoSelecionado}
          onIncluir={() => setModalSubTipo({ show: true, subTipo: null })}
          onEditar={(sub) => setModalSubTipo({ show: true, subTipo: sub })}
          onExcluir={handleExcluirSubTipo}
        />
      </Container>

      <ModalFormTipo
        show={modalTipo.show}
        tipo={modalTipo.tipo}
        onConfirmar={handleConfirmarTipo}
        onCancelar={() => setModalTipo({ show: false, tipo: null })}
      />

      <ModalFormSubTipo
        show={modalSubTipo.show}
        subTipo={modalSubTipo.subTipo}
        tipoSelecionado={tipoSelecionado}
        onConfirmar={handleConfirmarSubTipo}
        onCancelar={() => setModalSubTipo({ show: false, subTipo: null })}
      />

      <ModalConfirmacao
        show={modalExcluir.show}
        title="Confirmar exclusão"
        message={modalExcluir.message}
        variant="danger"
        labelCancel="Cancelar"
        labelConfirm="Excluir"
        onClose={handleFecharExcluir}
      />
    </>
  );
};

export default ResultadoAtendimento;
